import Slider, { SliderProps } from 'rc-slider'
import 'rc-slider/assets/index.css'
import { cloneElement } from 'react'
import { Tooltip } from 'react-tooltip'
import { isNotNil } from 'ramda'
import styled from 'styled-components'
import { Box } from '../../../shared_components/UI/Box'
import { H3, H4, P, Span } from '../../../shared_components/Library/Typeography'
import { isTgeSoon, useStakingContext } from '../../../contexts/StakingContext'

export const MAX_MONTHS = 12

export function TokensStakeStep({
  howManyTokens,
  setHowManyTokens,
}: {
  howManyTokens: number
  setHowManyTokens: (value: number) => void
}) {
  const { tokensAvailable } = useStakingContext()

  return (
    <StakeStep
      index={1}
      isTbc={isTgeSoon}
      title="How many tokens?"
      description={'Increase the amount you earn by staking more tokens'}
      amount={howManyTokens}
      amountDescription={'Tokens'}
      slider={{
        marks: {
          0: <CustomMark value={0} activeValue={howManyTokens} label="0" />,
          [Number(tokensAvailable)]: (
            <CustomMark
              value={tokensAvailable}
              activeValue={howManyTokens}
              label={tokensAvailable.toLocaleString()}
            />
          ),
        },
        min: tokensAvailable > 100 ? 100 : 0, // fixes: nodeWithTooltip position if tokens less than 100
        max: Number(tokensAvailable),
        step: Math.floor(tokensAvailable * 0.1),
        defaultValue: Number(tokensAvailable),
        value: Number(howManyTokens),
        barStepsHidden: true,
        onChange: (value: number) => setHowManyTokens(value),
        onChangeComplete: (value: number) => setHowManyTokens(value),
        disabled: tokensAvailable <= 100,
      }}
    />
  )
}

export function VaultsStakeStep({
  howManyVaults,
  handleHowManyVaultsChange,
}: {
  howManyVaults: number
  handleHowManyVaultsChange: (value: number) => void
}) {
  const { vaultsAvailable } = useStakingContext()

  return (
    <StakeStep
      index={1}
      isTbc={isTgeSoon}
      title={'How many vaults?'}
      description={
        'Select the number of Vaults you’d like to stake. More vaults equals more rewards.'
      }
      amount={howManyVaults}
      amountDescription={`of ${vaultsAvailable}`}
      slider={{
        marks: {
          0: <CustomMark value={0} activeValue={howManyVaults} label="0" />,
          [Number(vaultsAvailable)]: (
            <CustomMark
              value={vaultsAvailable}
              activeValue={howManyVaults}
              label={vaultsAvailable.toLocaleString()}
            />
          ),
        },
        min: 0,
        max: Number(vaultsAvailable),
        step: Math.floor(1),
        defaultValue: Number(vaultsAvailable),
        value: Number(howManyVaults),
        barStepsHidden: true,
        onChange: (value: number) => handleHowManyVaultsChange(value),
        onChangeComplete: (value: number) => handleHowManyVaultsChange(value),
        disabled: false,
      }}
    />
  )
}

export function VaultsTierStakeStep({
  howManyMonths,
  setHowManyMonths,
  howManyVaults,
  multiplier,
}) {
  const { vaultsAvailable } = useStakingContext()

  return (
    <StakeStep
      index={2}
      isTbc={isTgeSoon}
      title="How long for?"
      description="Select from 3, 6 or 12 months. Longer periods compound larger rewards."
      amount={multiplier}
      amountSuffix="x"
      amountDescription="multiplier"
      slider={{
        marks: {
          0: <CustomMark value={0} activeValue={howManyMonths} label="0" />,
          4: <CustomMark value={4} activeValue={howManyMonths} label="3" />,
          8: <CustomMark value={8} activeValue={howManyMonths} label="6" />,
          12: <CustomMark value={12} activeValue={howManyMonths} label="12" />,
        },
        min: 0,
        max: MAX_MONTHS,
        step: null,
        defaultValue: vaultsAvailable > 0 ? MAX_MONTHS : 0,
        value: howManyMonths,
        disabled: howManyVaults === 0,
        barStepsHidden: true,
        onChangeComplete: (value: number) => setHowManyMonths(value),
        onChange: (value: number) => setHowManyMonths(value),
        tooltipFormatter: (value: number) => {
          const monthsMap = {
            0: '0',
            4: '3 months',
            8: '6 months',
            12: '12 months',
          }
          return monthsMap[value]
        },
      }}
    />
  )
}

export function TokensTierStakeStep({
  howManyMonths,
  setHowManyMonths,
  howManyTokens,
  multiplier,
}) {
  const { tokensAvailable } = useStakingContext()

  return (
    <StakeStep
      index={2}
      isTbc={isTgeSoon}
      title="How long for?"
      description={'Your APY increases the longer you lock in for'}
      amount={multiplier}
      amountSuffix="x"
      amountDescription="multiplier"
      slider={{
        marks: {
          0: <CustomMark value={0} activeValue={howManyMonths} label="0" />,
          4: <CustomMark value={4} activeValue={howManyMonths} label="3" />,
          8: <CustomMark value={8} activeValue={howManyMonths} label="6" />,
          12: <CustomMark value={12} activeValue={howManyMonths} label="12" />,
        },
        min: 0,
        max: MAX_MONTHS,
        step: null,
        defaultValue: tokensAvailable > 0 ? MAX_MONTHS : 0,
        value: howManyMonths,
        disabled: howManyTokens <= 100 || tokensAvailable <= 100,
        barStepsHidden: true,
        onChangeComplete: (value: number) => setHowManyMonths(value),
        onChange: (value: number) => setHowManyMonths(value),
        tooltipFormatter: (value: number) => {
          const monthsMap = {
            0: '0',
            4: '3 months',
            8: '6 months',
            12: '12 months',
          }
          return monthsMap[value]
        },
      }}
    />
  )
}

export function StakeStep({
  index,
  title,
  description,
  amount,
  amountSuffix,
  amountDescription,
  slider,
  isTbc,
}: {
  index: number
  title: string
  description: string
  amount: number | null
  amountSuffix?: string
  amountDescription?: string
  slider: {
    defaultValue: SliderProps['defaultValue']
    value?: SliderProps['value']
    min: SliderProps['min']
    max: SliderProps['max']
    step: SliderProps['step']
    marks: SliderProps['marks']
    disabled?: SliderProps['disabled']
    onChangeComplete: SliderProps['onChangeComplete']
    onChange: SliderProps['onChange']
    tooltipFormatter?: (value: number) => string
    barStepsHidden?: boolean
  }
  isTbc?: boolean
}) {
  const tipHandleRender: SliderProps['handleRender'] = (node, handleProps) => {
    const nodeWithTooltipId = cloneElement(node, {
      'data-tooltip-id': `slider-${index}-tooltip`,
      id: 'rc-slider-custom-handle',
      children: <SliderHandleIconSVG />,
      style: {
        ...node.props.style,
      },
    })
    if (handleProps.value === 0) return nodeWithTooltipId

    return (
      <>
        {nodeWithTooltipId}
        {handleProps.dragging && (
          <Tooltip
            id={`slider-${index}-tooltip`}
            isOpen={true}
            opacity={1}
            place="top"
            positionStrategy="fixed"
            style={{
              border: '1px solid white',
              zIndex: 1,
            }}
          >
            {slider.tooltipFormatter?.(handleProps.value) ?? handleProps.value}
          </Tooltip>
        )}
      </>
    )
  }

  return (
    <Box dc g={20}>
      <Box jsb>
        <Box dc g={8} mw="285px">
          <Box g={4} align="baseline">
            <Span s="XL" c="text-secondary" fw="regular">
              {index}.
            </Span>
            <H3 s="L" fw="bolder" c="text-primary" ff="Proxima Nova" uc>
              {title}
            </H3>
          </Box>
          <P s="M" c="text-secondary" fw="regular">
            {description}
          </P>
        </Box>
        {isTbc ? (
          <Box g={6} ac jfe fu aw>
            <H4 s="XL" c="text-brandPrimary" fw="bolder" ff="Proxima Nova" uc>
              TBC
            </H4>
          </Box>
        ) : (
          <>
            {isNotNil(amount) && (
              <Box g={6} ac jfe fu aw>
                <H4
                  s="XL"
                  c={Number(amount) === 0 ? 'text-error' : 'text-brandPrimary'}
                  fw="bolder"
                  ff="Proxima Nova"
                  uc
                >
                  {amount.toLocaleString()}
                  {amountSuffix ?? ''}
                </H4>
                <Span s="M" c="text-secondary" fw="regular">
                  {amountDescription}
                </Span>
              </Box>
            )}
          </>
        )}
      </Box>
      <SliderBox barStepsHidden={slider.barStepsHidden}>
        <Slider
          disabled={isTbc || slider.disabled}
          defaultValue={isTbc ? 0 : slider.defaultValue}
          value={isTbc ? 0 : slider.value}
          min={slider.min}
          max={isTbc ? 0 : slider.max}
          step={slider.step}
          onChangeComplete={slider.onChangeComplete}
          onChange={slider.onChange}
          marks={isTbc ? undefined : slider.marks}
          handleRender={tipHandleRender}
        ></Slider>
      </SliderBox>
    </Box>
  )
}

const SliderBox = styled(Box)<{ barStepsHidden?: boolean }>`
  ${({ barStepsHidden }) =>
    barStepsHidden &&
    `
    .rc-slider-step {
      display: none;
    }
  `}

  .rc-slider {
    height: 28px;
  }

  .rc-slider-rail,
  .rc-slider-track {
    height: 16px;
    border-radius: var(--units-88, 88px);
  }

  .rc-slider-disabled {
    opacity: 0.5;
    background-color: unset;
  }

  .rc-slider-rail {
    border: 1px solid white;

    background: var(--surface-primary, #080808);
    --dotSize: 0.5px;

    ::after {
      /* Cover our element */
      content: '';
      position: absolute;
      inset: 0;
      opacity: 0.3;

      /* Dotted background */
      background-image: radial-gradient(
        circle at center,
        white var(--dotSize),
        transparent 0
      );
      background-size: 2px 2px;
      background-repeat: round;
      background-position: 0 0, 0.65rem 0.65rem;

      z-index: 1;
    }
  }

  .rc-slider-track {
    background: var(--surface-brand, #FFF00);
    ::after {
      /* Cover our element */
      content: '';
      position: absolute;
      inset: 0;
      opacity: 0.3;

      /* Dotted background */
      background-image: radial-gradient(
        circle at center,
        black var(--dotSize),
        transparent 0
      );
      background-size: 3px 3px;
      background-repeat: round;
      background-position: 0 0, 0.65rem 0.65rem;

      z-index: 1;
    }
  }

  #rc-slider-custom-handle {
    opacity: 1;
    height: 44px;
    width: 44px;
    border: solid 4px black;
    margin-top: -14px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:active {
      box-shadow: 0 0 5px var(--surface-brand, #FFF00);
    }

    &:focus-visible {
      box-shadow: 0 0 0 3px var(--surface-brand, #FFF00);
    }

    &.rc-slider-handle-dragging {
      box-shadow: 0 0 5px var(--surface-brand, #FFF00);
    }
  }

  .rc-slider-mark {
    top: 36px;
  }

  .react-tooltip {
    border: 1px solid white;
    border-radius: 8px;
  }
  .react-tooltip-arrow {
    border: 1px solid white;
    border-left-color: transparent;
    border-top-color: transparent;
  }
`

function SliderHandleIconSVG() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="16"
      viewBox="0 0 18 16"
      fill="none"
    >
      <rect
        x="15.5"
        width="2"
        height="16"
        rx="1"
        fill="black"
        fillOpacity="0.08"
      />
      <rect
        x="8.5"
        width="2"
        height="16"
        rx="1"
        fill="black"
        fillOpacity="0.08"
      />
      <rect
        x="0.5"
        width="2"
        height="16"
        rx="1"
        fill="black"
        fillOpacity="0.08"
      />
    </svg>
  )
}

function CustomMark({
  value,
  activeValue,
  label,
}: {
  value: number | bigint
  activeValue: number | bigint
  label: string
}) {
  const isActive = value === activeValue
  const isZero = value === 0
  return (
    <span
      style={{
        color: isActive
          ? isZero
            ? '#FF5252'
            : 'var(--surface-brand, #FFF00)'
          : 'var(--text-secondary, #B1B1B1)',
      }}
    >
      {label}
    </span>
  )
}
